import { Menu } from '@headlessui/react';
import { useRouter } from 'next/router';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleStackIcon,
  BarsArrowUpIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { format } from 'timeago.js';
import Link from 'next/link';
import { Badge, Button } from '@missing-no/ui';
import { Layout } from '../../components/common';
import { useTeam } from '../../lib/hooks/teams';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function TeamDashboard() {
  const router = useRouter();
  const { teamSlug } = router.query;

  const { team, isLoading, isError } = useTeam(teamSlug as string);

  if (isError) {
    return <div>error</div>;
  }

  return (
    <>
      {/* 3 column wrapper */}
      <div className="flex-grow w-full max-w-7xl mx-auto xl:px-8 lg:flex">
        {/* Left sidebar & main wrapper */}
        <div className="flex-1 min-w-0 bg-white xl:flex">
          {/* Account profile */}
          <div className="xl:flex-shrink-0 xl:w-64 xl:border-r xl:border-gray-200 bg-white">
            <div className="pl-4 pr-6 py-6 sm:pl-6 lg:pl-8 xl:pl-0">
              <div className="flex items-center justify-between">
                <div className="flex-1 space-y-8">
                  <div className="space-y-8 sm:space-y-0 sm:flex sm:justify-between sm:items-center xl:block xl:space-y-8">
                    {/* Profile */}
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 h-12 w-12">
                        {team?.pictureUrl ? (
                          <img
                            className="rounded-full"
                            src={team?.pictureUrl}
                            alt=""
                          />
                        ) : (
                          <UserGroupIcon />
                        )}
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          {team?.name || teamSlug}
                        </div>
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row xl:flex-col">
                      <Link href={`/${teamSlug}/new`} passHref legacyBehavior>
                        <Button disabled className="xl:w-full" type="primary">
                          New Campaign
                        </Button>
                      </Link>

                      <Link href={`/${teamSlug}/settings`} passHref>
                        <Button
                          disabled
                          className="mt-3 sm:mt-0 sm:ml-3 xl:ml-0 xl:mt-3 xl:w-full"
                        >
                          Team Settings
                        </Button>
                      </Link>
                    </div>
                  </div>
                  {/* Meta info */}
                  <div className="flex flex-col space-y-6 sm:flex-row sm:space-y-0 sm:space-x-8 xl:flex-col xl:space-x-0 xl:space-y-6">
                    {/* <div className="flex items-center space-x-2">
                    <CheckBadgeIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 font-medium">
                      Verified
                    </span>
                  </div> */}
                    <div className="flex items-center space-x-2">
                      <CircleStackIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-gray-500 font-medium">
                        {isLoading || !team
                          ? ''
                          : `${team.campaigns.length} Campaign(s)`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Projects List */}
          <div className="bg-white lg:min-w-0 lg:flex-1">
            <div className="pl-4 pr-6 pt-4 pb-4 border-b border-t border-gray-200 sm:pl-6 lg:pl-8 xl:pl-6 xl:pt-6 xl:border-t-0">
              <div className="flex items-center">
                <h1 className="flex-1 text-lg font-medium">Campaigns</h1>
                <Menu as="div" className="relative">
                  <Menu.Button className="w-full bg-white border border-gray-300 rounded-md shadow-sm px-4 py-2 inline-flex justify-center text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rum-500">
                    <BarsArrowUpIcon
                      className="mr-3 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                    Sort
                    <ChevronDownIcon
                      className="ml-2.5 -mr-1.5 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </Menu.Button>
                  <Menu.Items className="origin-top-right z-10 absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-700',
                              'block px-4 py-2 text-sm',
                            )}
                          >
                            Name
                          </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-700',
                              'block px-4 py-2 text-sm',
                            )}
                          >
                            Date modified
                          </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active
                                ? 'bg-gray-100 text-gray-900'
                                : 'text-gray-700',
                              'block px-4 py-2 text-sm',
                            )}
                          >
                            Date created
                          </a>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Menu>
              </div>
            </div>
            <ul
              role="list"
              className="relative z-0 divide-y divide-gray-200 border-b border-gray-200"
            >
              {isLoading
                ? 'Loading'
                : team.campaigns.map((campaign, i) => {
                    const href = `/${teamSlug}/${campaign.slug}`;

                    return (
                      <li
                        key={campaign.slug}
                        className="relative pl-4 pr-6 py-5 hover:bg-gray-50 sm:py-6 sm:pl-6 lg:pl-8 xl:pl-6"
                      >
                        <div className="flex items-center justify-between space-x-4">
                          {/* Repo name and link */}
                          <div className="min-w-0 space-y-3">
                            <div className="flex items-center space-x-3">
                              <span
                                className={classNames(
                                  i % 2 ? 'bg-green-100' : 'bg-gray-100',
                                  'h-4 w-4 rounded-full flex items-center justify-center',
                                )}
                                aria-hidden="true"
                              >
                                <span
                                  className={classNames(
                                    i % 2 ? 'bg-green-400' : 'bg-gray-400',
                                    'h-2 w-2 rounded-full',
                                  )}
                                />
                              </span>

                              <span className="block">
                                <h2 className="text-sm font-medium">
                                  <Link href={href} passHref>
                                    <span
                                      className="absolute inset-0"
                                      aria-hidden="true"
                                    />
                                    {campaign.name}{' '}
                                    <span className="sr-only">
                                      {i % 2 ? 'Running' : 'Not running'}
                                    </span>
                                  </Link>
                                </h2>
                              </span>
                            </div>
                            <div className="relative group flex items-center space-x-2.5">
                              {campaign.tags.map((tag) => (
                                <Badge key={tag} value={tag} />
                              ))}
                            </div>
                          </div>
                          <div className="sm:hidden">
                            <ChevronRightIcon
                              className="h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </div>
                          {/* Repo meta info */}
                          <div className="hidden sm:flex flex-col flex-shrink-0 items-end space-y-3">
                            <p className="flex items-center space-x-4">
                              <span className="relative text-sm text-gray-500 hover:text-gray-900 font-medium">
                                {campaign.description || ' '}
                              </span>
                            </p>
                            <p className="flex text-gray-500 text-sm space-x-2">
                              <span>
                                Last activity {format(campaign.updatedAt)}
                              </span>
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
            </ul>
          </div>
        </div>
        {/* Activity feed */}
        <div className="bg-gray-50 pr-4 sm:pr-6 lg:pr-8 lg:flex-shrink-0 lg:border-l lg:border-gray-200 xl:pr-0"></div>
      </div>
    </>
  );
}

TeamDashboard.Layout = Layout;
